import React from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import SeoHead from "../components/SeoHead";

const permittedUses = [
  "Software development",
  "Quality assurance testing",
  "User acceptance testing",
  "Email workflow testing",
  "Spam protection",
  "Privacy protection",
  "Educational purposes",
];

const prohibitedSections = [
  {
    title: "Spam",
    desc: "Sending or supporting unsolicited communications.",
  },
  {
    title: "Phishing",
    desc: "Collecting credentials, personal information, or financial information.",
  },
  {
    title: "Fraud",
    desc: "Creating deceptive accounts or participating in fraudulent activities.",
  },
  {
    title: "Malware",
    desc: "Distributing malicious software.",
  },
  {
    title: "Illegal activities",
    desc: "Any activity prohibited by applicable laws.",
  },
  {
    title: "Platform abuse",
    desc: "Attempting to overload systems, bypass rate limits, scrape infrastructure, or attack services.",
  },
  {
    title: "Impersonation",
    desc: "Misrepresenting identity or affiliation.",
  },
];

function AcceptableUse() {
  return (
    <Layout>
      <SeoHead
        title="Acceptable Use Policy – Ghost Inbox"
        description="Ghost Inbox Acceptable Use Policy. Learn permitted use cases for software testing, QA, and privacy protection, and prohibited activities including spam, fraud, and phishing."
        path="/acceptable-use"
      />

      <div className="mx-auto max-w-3xl px-6 py-16">
        <p className="text-xs font-medium uppercase tracking-wider text-emerald-400">Legal</p>
        <h1 className="mt-2 text-3xl font-bold text-white">Acceptable Use Policy</h1>
        <p className="mt-4 text-sm text-zinc-500">Last updated: 10 June 2026</p>
        <p className="mt-4 leading-relaxed text-zinc-400">
          Ghost Inbox provides temporary email inbox services for software testing, quality
          assurance, privacy protection, and legitimate business purposes. By using Ghost Inbox,
          you agree to comply with this Acceptable Use Policy in addition to our{" "}
          <Link to="/terms" className="text-emerald-400 hover:underline">
            Terms of Service
          </Link>
          .
        </p>

        <section className="mt-12 rounded-xl border border-zinc-800 bg-zinc-900/40 p-6">
          <h2 className="text-lg font-semibold text-white">Permitted uses</h2>
          <p className="mt-3 text-sm text-zinc-400">Users may use Ghost Inbox for:</p>
          <ul className="mt-4 list-disc space-y-2 pl-5 text-sm text-zinc-400">
            {permittedUses.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>

        <section className="mt-6 rounded-xl border border-zinc-800 bg-zinc-900/40 p-6">
          <h2 className="text-lg font-semibold text-white">Prohibited activities</h2>
          <p className="mt-3 text-sm text-zinc-400">Users may not use Ghost Inbox for:</p>
          <div className="mt-4 space-y-4">
            {prohibitedSections.map((item) => (
              <div key={item.title}>
                <h3 className="text-sm font-medium text-zinc-300">{item.title}</h3>
                <p className="mt-1 text-sm text-zinc-400">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-6 rounded-xl border border-zinc-800 bg-zinc-900/40 p-6">
          <h2 className="text-lg font-semibold text-white">Enforcement</h2>
          <p className="mt-3 text-sm leading-relaxed text-zinc-400">
            To maintain platform integrity, Ghost Inbox reserves the right to monitor abuse
            indicators, block suspicious traffic, restrict abusive users, and remove content that
            violates this policy. Violations may result in immediate suspension or termination of
            access without notice.
          </p>
        </section>

        <div className="mt-12 flex flex-col gap-3 rounded-xl border border-zinc-800 bg-zinc-900/40 p-6 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-zinc-400">
            Questions? Visit our{" "}
            <Link to="/contact" className="text-emerald-400 hover:underline">
              Contact Us
            </Link>{" "}
            page.
          </p>
          <Link
            to="/terms"
            className="inline-block rounded-lg border border-zinc-700 px-5 py-2.5 text-center text-sm text-zinc-300 hover:border-zinc-500"
          >
            Terms of Service
          </Link>
        </div>
      </div>
    </Layout>
  );
}

export default AcceptableUse;
